/**
 * Modal de checkout multi-grupo para el carrito de pagos del residente.
 *
 * Recibe N payments seleccionados. Usa el provider activo para agruparlos
 * (por edificio en el caso `manualTransfer`). Por cada grupo el residente:
 *   1. Ve la cuenta bancaria del edificio destino.
 *   2. Hace la transferencia por afuera.
 *   3. Sube el comprobante en el slot del grupo.
 *
 * Cuando todos los grupos tienen comprobante, el botón "Confirmar pagos"
 * dispara `provider.submitProof(...)` para cada grupo y marca los N
 * payments como `pendiente_verificacion` con el mismo `cartGroupId`.
 *
 * Si solo hay 1 grupo (caso single-edificio), la UI se simplifica para que
 * se parezca al modal de pago individual viejo.
 */
import React, { useEffect, useMemo, useState } from "react";
import { FaUpload, FaCheckCircle, FaFileAlt, FaBuilding } from "react-icons/fa";
import { getActiveProvider } from "../services/paymentProviders";
import { auth } from "../firebase";
import { useIsReadOnly } from "../context/EffectiveIdentityContext";
import {
  colors,
  spacing,
  radius,
  shadows,
  typography,
} from "../utils/designTokens";
import AdminModal from "./AdminModal";

const formatCurrencyUY = (n) =>
  "$ " + Number(n || 0).toLocaleString("es-UY", { maximumFractionDigits: 2 });

const PaymentCheckoutModal = ({ open, onClose, payments, buildings, onSuccess, showToast }) => {
  const provider = getActiveProvider();
  // "Ver como residente": modo solo lectura. Bloquea el envío de comprobantes.
  const readOnly = useIsReadOnly();
  const [groupProofs, setGroupProofs] = useState({}); // { groupId: File }
  const [submitting, setSubmitting] = useState(false);
  const [errorByGroup, setErrorByGroup] = useState({});

  const groups = useMemo(() => {
    if (!payments || payments.length === 0) return [];
    return provider.groupPayments(payments, { buildings });
  }, [payments, buildings, provider]);

  // Limpiar comprobantes cuando cambia la lista de pagos.
  useEffect(() => {
    setGroupProofs({});
    setErrorByGroup({});
  }, [payments]);

  const allProofsReady = useMemo(() => {
    if (!provider.needsProofPerGroup) return true;
    return groups.every((g) => groupProofs[g.groupId]);
  }, [groups, groupProofs, provider]);

  const handleProofChange = (groupId, file) => {
    if (!file) {
      const next = { ...groupProofs };
      delete next[groupId];
      setGroupProofs(next);
      return;
    }
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      showToast?.("El comprobante no puede superar los 10 MB.", "error");
      return;
    }
    const allowed = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!allowed.includes(file.type)) {
      showToast?.("Solo se permiten JPG, PNG, WebP o PDF.", "error");
      return;
    }
    setGroupProofs({ ...groupProofs, [groupId]: file });
  };

  const handleConfirm = async () => {
    if (readOnly) { showToast?.("Estás en modo solo lectura: no podés hacer acciones."); return; }
    setSubmitting(true);
    setErrorByGroup({});
    const user = auth.currentUser;
    const errs = {};
    let processedGroups = 0;
    for (const group of groups) {
      try {
        const cartGroupId = `${group.groupId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        await provider.submitProof({
          group,
          proofFile: groupProofs[group.groupId] || null,
          user,
          cartGroupId,
        });
        processedGroups += 1;
      } catch (err) {
        console.error(`[PaymentCheckoutModal] grupo ${group.groupId} fallo:`, err);
        errs[group.groupId] = err.message || String(err);
      }
    }
    setSubmitting(false);
    if (Object.keys(errs).length > 0) {
      setErrorByGroup(errs);
      showToast?.(
        `Se procesaron ${processedGroups} de ${groups.length} grupos. Revisá los marcados en rojo.`,
        "warning"
      );
      return;
    }
    showToast?.(
      groups.length === 1
        ? "Pago enviado. Queda pendiente de verificación por la administración."
        : `${groups.length} pagos enviados. Quedan pendientes de verificación.`,
      "success"
    );
    onSuccess?.();
    onClose?.();
  };

  if (!open) return null;

  const totalGlobal = groups.reduce((s, g) => s + g.totalAmount, 0);

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title={groups.length > 1 ? "Pagar varios" : "Pagar"}
      subtitle={
        groups.length > 1
          ? `${payments.length} pagos en ${groups.length} edificio(s) · Total ${formatCurrencyUY(totalGlobal)}`
          : null
      }
      size="medium"
      icon={<FaFileAlt />}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={styles.btnSecondary}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || !allProofsReady || readOnly}
            style={{
              ...styles.btnPrimary,
              opacity: submitting || !allProofsReady || readOnly ? 0.6 : 1,
            }}
          >
            {submitting
              ? "Enviando..."
              : groups.length > 1
              ? `Confirmar ${groups.length} pagos`
              : "Enviar pago"}
          </button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: spacing[4] }}>
        <p style={styles.intro}>
          {groups.length > 1
            ? "Cada edificio tiene su propia cuenta bancaria. Hacé una transferencia por cada edificio y subí el comprobante correspondiente acá."
            : "Hacé la transferencia a la cuenta del edificio y subí el comprobante."}
        </p>

        {groups.map((group) => (
          <GroupCard
            key={group.groupId}
            group={group}
            proofFile={groupProofs[group.groupId]}
            onProofChange={(file) => handleProofChange(group.groupId, file)}
            error={errorByGroup[group.groupId]}
            disabled={submitting}
          />
        ))}
      </div>
    </AdminModal>
  );
};

const GroupCard = ({ group, proofFile, onProofChange, error, disabled }) => {
  return (
    <div style={styles.groupCard}>
      <div style={styles.groupHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FaBuilding style={{ color: colors.primary }} />
          <strong style={{ fontSize: 14, color: colors.black }}>
            {group.buildingName}
          </strong>
        </div>
        <span style={{ fontSize: 14, fontWeight: 400, color: colors.primary }}>
          {formatCurrencyUY(group.totalAmount)}
        </span>
      </div>

      {group.bankAccounts && group.bankAccounts.length > 0 ? (
        <div style={styles.bankAccounts}>
          <div style={styles.bankAccountsTitle}>Transferir a:</div>
          {group.bankAccounts.map((acc, i) => (
            <div key={acc.id || i} style={styles.bankAccountRow}>
              {acc.bank} · {acc.accountType} · {acc.accountNumber} ({acc.currency || "UYU"})
            </div>
          ))}
        </div>
      ) : (
        <div style={{ ...styles.bankAccounts, color: colors.warning }}>
          Sin cuenta bancaria configurada — contactá al administrador del edificio.
        </div>
      )}

      <div style={styles.paymentsList}>
        <div style={styles.bankAccountsTitle}>Pagos incluidos:</div>
        {group.payments.map((p) => {
          const remaining = Number(p.remainingAmount);
          const due =
            !isNaN(remaining) && remaining > 0
              ? remaining
              : Number(p.amount) || 0;
          return (
            <div key={p.id} style={styles.paymentItem}>
              <span style={{ fontSize: 13, color: colors.gray700 }}>
                {p.description}
                {p.apartmentNumber ? ` · Unidad ${p.apartmentNumber}` : ""}
              </span>
              <span style={{ fontSize: 13, color: colors.gray900 }}>
                {formatCurrencyUY(due)}
              </span>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: spacing[3] }}>
        <label style={styles.uploadLabel}>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={(e) => onProofChange(e.target.files[0] || null)}
            style={{ display: "none" }}
            disabled={disabled}
          />
          <span style={styles.uploadButton}>
            {proofFile ? <FaCheckCircle style={{ color: colors.success }} /> : <FaUpload />}
            {proofFile ? proofFile.name : "Subir comprobante"}
          </span>
        </label>
        {error && (
          <p style={{ color: colors.error, fontSize: 12, marginTop: 6 }}>
            Error al procesar este grupo: {error}
          </p>
        )}
      </div>
    </div>
  );
};

const styles = {
  intro: {
    margin: 0,
    fontSize: 13,
    color: colors.gray600,
    lineHeight: 1.5,
  },
  groupCard: {
    background: colors.bgCard,
    borderRadius: radius.md,
    padding: spacing[5],
    boxShadow: shadows.sm,
    display: "flex",
    flexDirection: "column",
    gap: spacing[3],
  },
  groupHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  bankAccounts: {
    background: colors.bgMain,
    borderRadius: radius.sm,
    padding: spacing[3],
    fontSize: 13,
    color: colors.gray900,
  },
  bankAccountsTitle: {
    fontSize: 11,
    color: colors.gray600,
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  bankAccountRow: {
    fontSize: 13,
    color: colors.gray900,
    padding: "2px 0",
  },
  paymentsList: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  paymentItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 0",
    borderBottom: `1px solid ${colors.gray100}`,
  },
  uploadLabel: {
    display: "inline-block",
    width: "100%",
    cursor: "pointer",
  },
  uploadButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    border: `1px dashed ${colors.gray300}`,
    borderRadius: radius.sm,
    padding: "10px 14px",
    color: colors.gray700,
    fontSize: 13,
    fontFamily: typography.fontFamily,
    background: colors.bgCard,
    width: "100%",
    justifyContent: "center",
  },
  btnPrimary: {
    background: colors.primary,
    color: "#fff",
    border: "none",
    borderRadius: radius.sm,
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: typography.fontFamily,
  },
  btnSecondary: {
    background: colors.bgCard,
    color: colors.gray900,
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.sm,
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: typography.fontFamily,
  },
};

export default PaymentCheckoutModal;
